import { Text } from "@mantine/core";
import { Dropzone } from "@mantine/dropzone";
import { useRef } from "react";
import {
  DropzoneWrapper,
  FileText,
  FileUpload,
  StyledDropZone,
  StyledGroup,
  XError,
} from "./styles";
import { useFileDrop } from "./use-file-drop";
import { t } from "i18next";

interface FileDropProps {
  fileExtenstions: string[];
  description: string;
  load: (files: File[]) => void;
}

export const FileDrop = ({
  fileExtenstions,
  description,
  load,
}: FileDropProps) => {
  const { handleFileAccept, handleFileReject } = useFileDrop(load);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Custom validator to check file extensions
  const validator = (file: any) => {
    // During drag events, Mantine passes DataTransferItem which doesn't have a name property
    // We can't validate by extension at this stage, so accept all files
    if (!file || !('name' in file)) {
      // Accept all files during drag - validation will happen on actual drop
      return null;
    }
    
    // Actual File object validation (on drop)
    const extensions = fileExtenstions.filter(ext => ext.startsWith('.'));
    const fileName = file.name.toLowerCase();
    const isValid = extensions.some(ext => fileName.endsWith(ext.toLowerCase()));
    
    if (!isValid) {
      return {
        code: 'invalid-file-type',
        message: `File type not supported. Allowed types: ${extensions.join(', ')}`
      };
    }
    
    return null;
  };

  // Get accept string for native file input (use only extensions for better browser support)
  const getNativeAcceptString = () => {
    // Extract only file extensions (those starting with .)
    const extensions = fileExtenstions.filter(ext => ext.startsWith('.'));
    return extensions.join(',');
  };

  const handleClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    fileInputRef.current?.click();
  };

  const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (files && files.length > 0) {
      handleFileAccept(Array.from(files) as any);
    }
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const nativeAccept = getNativeAcceptString();

  return (
    <DropzoneWrapper>
      <input
        ref={fileInputRef}
        type="file"
        accept={nativeAccept}
        multiple
        style={{ display: 'none' }}
        onChange={handleFileInputChange}
      />
      <div onClick={handleClick} style={{ width: "100%" }}>
        <StyledDropZone
          multiple={true}
          onDrop={handleFileAccept}
          onReject={handleFileReject}
          validator={validator}
          activateOnClick={false}
          style={{ display: "flex", alignItems: "center", cursor: "pointer" }}
        >
          <StyledGroup
            spacing="xs"
            style={{ width: "100%", justifyContent: "flex-start" }}
          >
            <Dropzone.Accept>
              <FileUpload style={{ display: "flex" }} size={50} />
            </Dropzone.Accept>
            <Dropzone.Reject>
              <XError style={{ display: "flex" }} size={50} />
            </Dropzone.Reject>
            <Dropzone.Idle>
              <FileText style={{ display: "flex" }} size={50} />
            </Dropzone.Idle>
            <div style={{ marginLeft: "1rem", flex: 1 }}>
              <Text size="lg" inline>
                {t("title.dropFile")}
              </Text>
              <Text size="md" color="dimmed" inline mt={7}>
                {description}
              </Text>
            </div>
          </StyledGroup>
        </StyledDropZone>
      </div>
    </DropzoneWrapper>
  );
};
